import React, { createContext, MutableRefObject, ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

export type StackVideoPlayer = {
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
};

type StackVideoPlayerContextValue = {
  controlledPlayerRef: MutableRefObject<StackVideoPlayer | null>;
  controlledPlayerElementRef: MutableRefObject<HTMLElement | null>;
  setControlledPlayer: (player: StackVideoPlayer | null) => void;
  setControlledPlayerElement: (element: HTMLElement | null) => void;
};

const StackVideoPlayerContext = createContext<StackVideoPlayerContextValue | null>(null);

export const StackVideoPlayerContextProvider = ({ children }: { children: ReactNode }) => {
  const controlledPlayerRef = useRef<StackVideoPlayer | null>(null);
  const controlledPlayerElementRef = useRef<HTMLElement | null>(null);

  const setControlledPlayer = useCallback((player: StackVideoPlayer | null) => {
    controlledPlayerRef.current = player;
  }, []);

  const setControlledPlayerElement = useCallback((element: HTMLElement | null) => {
    controlledPlayerElementRef.current = element;
  }, []);

  const value = useMemo(
    () => ({
      controlledPlayerRef,
      controlledPlayerElementRef,
      setControlledPlayer,
      setControlledPlayerElement,
    }),
    [setControlledPlayer, setControlledPlayerElement]
  );

  return <StackVideoPlayerContext.Provider value={value}>{children}</StackVideoPlayerContext.Provider>;
};

export const useStackVideoPlayer = () => useContext(StackVideoPlayerContext);

// Backwards-compatible alias for existing imports.
export const useStackVideoPlayerContext = () => useStackVideoPlayer();
